/**
 * @fileoverview Prompt template for inspiration-attribute-deriver-v1.
 *
 * Analyzes one or more photos of the same person and derives character
 * inspiration attributes based on the image mode (portrait, half-body,
 * full-body). Combines detailed visual analysis with the model's global
 * knowledge of the named subject.
 */

export const prompt = `You are an expert at analyzing photographs of people and extracting highly detailed physical attributes for fiction character inspiration profiles. You combine meticulous visual observation with your global knowledge of the subject to produce accurate, comprehensive results.

## TASK

Examine the provided {{imageCount}} image(s) of **{{inspirationName}}** and derive character attributes based on the image mode: **{{imageMode}}**.

Use BOTH your visual observations from the image(s) AND your existing knowledge of {{inspirationName}} to produce the most accurate result. Your knowledge is especially valuable for attributes that are difficult to determine from a single image (e.g., ethnicity, height, body proportions, typical demeanor). Where your visual observation conflicts with well-known facts, prefer the known facts. Where the image provides detail your knowledge does not cover, trust the image.

**If you do not recognize the name "{{inspirationName}}" or have no knowledge of this person, rely entirely on visual analysis of the image(s). Do not fabricate knowledge.**

## IMAGE VALIDATION (REQUIRED FIRST STEP)

Before extracting any attributes, validate the image(s):

{{#if isMultiImage}}
1. **Same Person Check**: All provided images MUST depict the same person. Compare facial structure, key features, and overall appearance across all images. If the images clearly show DIFFERENT people (different facial structure, different ethnicity, different age range, etc.), the set is INVALID.
2. **Person Present Check**: Each image must contain at least one person visible. Landscapes, objects, or animals without a person are INVALID.
3. **Divergence Check**: If the images show the same person but attributes diverge significantly across images in ways that cannot be reconciled (e.g., wildly different apparent ages, contradictory body types), the set is INVALID. Minor differences in lighting, angle, hairstyle, or makeup are expected and acceptable.
{{else}}
1. **Single Person Check**: The image must show exactly ONE person. If there are multiple people, it is INVALID.
2. **Person Present Check**: There must be at least one person visible. Landscapes, objects, or animals without a person are INVALID.
{{/if}}

If validation fails, return:
\`\`\`json
{
  "isValid": false,
  "error": "Explanation of why validation failed"
}
\`\`\`

{{#if isMultiImage}}
## MULTI-IMAGE ANALYSIS STRATEGY

You have {{imageCount}} images of the same person. Use them together to produce the most accurate attributes:

- **Leverage complementary angles**: Different images may show features more clearly. Use the clearest view for each attribute.
- **Resolve minor differences smartly**: If hair color appears slightly different across images due to lighting, pick the most natural/neutral representation. If one image shows more body than another, use the more revealing image for body attributes.
- **Prefer consensus**: When most images agree on an attribute, use that value. A single outlier image (e.g., unusual lighting) should not override the majority.
- **Descriptions should synthesize**: For faceDescription and bodyDescription, synthesize observations from ALL images into a single cohesive paragraph that captures the person most faithfully.
{{/if}}

## IMPORTANT: IGNORE CLOTHING

Do NOT factor the subject's clothing in the image into any of your analysis. Clothing, outfits, and attire are resolved elsewhere. Focus exclusively on the person's physical features, body, and face. Do not describe garments, fabrics, or how clothing fits.

## WHAT TO LOOK FOR

Analyze the image(s) thoroughly and systematically. The depth of your analysis drives the quality of the output. Depending on the image mode, examine the following:

{{#if (eq imageMode "portrait")}}
### Portrait Mode — Analyze items 1 through 7:
{{else if (eq imageMode "half-body")}}
### Half-Body Mode — Analyze items 1 through 7, plus 8 through 11:
{{else}}
### Full-Body Mode — Analyze all items 1 through 12:
{{/if}}

**1. Overall Facial Structure & Shape:**
- Primary face shape (e.g., oval, round, square, heart, long, diamond).
- Prominence and shape of the cheekbones (e.g., high, chiseled, soft, rounded).
- Jawline (e.g., strong, defined, soft, angular, receding).
- Chin (e.g., pointed, rounded, square, cleft).

**2. Eyes:**
- Eye shape (e.g., almond, round, monolid, hooded, downturned, upturned).
- Exact eye color and any distinct features (e.g., flecks, rings around the iris).
- Eye size and spacing (e.g., large, small, wide-set, close-set).
- Eyelids (e.g., smooth, creased, heavy) and eyelash length, thickness, and curl.

**3. Eyebrows:**
- Shape (e.g., arched, straight, curved, angled).
- Thickness (e.g., thick, bushy, thin, delicate).
- Color.
- General appearance (e.g., neatly groomed, untamed, furrowed).

**4. Nose:**
- Bridge (e.g., straight, aquiline/Roman, concave, slightly bumpy).
- Tip (e.g., pointed, rounded, upturned/celestial, downturned/hooked).
- Width (e.g., narrow, broad) and nostril appearance (e.g., flared, small).

**5. Mouth & Lips:**
- Lip shape and fullness (e.g., full, thin, plump upper/lower, bow-shaped, wide, narrow).
- Lip color.
- Expression (e.g., smiling, neutral, frowning, smirking) and teeth if visible (e.g., straight, gaps).

**6. Hair:**
- Length.
- Exact color, including highlights, lowlights, or color variations.
- Whether the hair color appears to be dyed or natural (look for visible roots, unnatural uniformity, or colors that don't match the person's complexion and eyebrows).
- Texture/type (e.g., pin-straight, soft waves, tight ringlets, loose curls, coily, kinky).
- Style, including part (e.g., middle, side), bangs/fringe (e.g., blunt, wispy, curtain), updo, or how it falls.
- Volume and thickness.

**7. Skin & Complexion:**
- Skin tone/color (see the skin tone scale below).
- Texture (e.g., smooth, clear, glowing, slightly textured).
- Prominent unique features (e.g., freckles, moles, birthmarks, dimples, scars, specific lines).

{{#if (ne imageMode "portrait")}}
**8. Body Type & Physique:**
- General build (see the shape and athleticism scales below).
- Proportions: torso relative to legs, shoulder-to-hip ratio.
- Level of muscular definition or softness (e.g., toned arms, soft midsection, lean frame).

**9. Neck, Shoulders & Arms:**
- Neck length and thickness (e.g., long and elegant, thick and muscular, short).
- Shoulder slope and width (e.g., broad, squared, sloping, narrow, rounded).
- If visible: arm shape and tone, hand appearance (e.g., veiny, delicate, rough/calloused).

**10. Posture & Pose:**
- Stance: how the subject is standing or sitting.
- Body language: tension and orientation (e.g., rigid, relaxed, open, closed off, dynamic).
- Hand placement (e.g., hands in pockets, arms crossed, resting on hips).

**11. Torso & Chest:**
- Chest breadth (see the chest scale below).
- Clavicle/neckline prominence.
- Pectoral or bust definition relative to frame.
- Waistline taper relative to chest.

{{#if (eq imageMode "full-body")}}
**12. Legs & Lower Body:**
- Leg length relative to torso (e.g., long-legged, short, proportional).
- Thigh shape and build (e.g., muscular, thick, slender, soft).
- Calf musculature and ankle thickness if visible.
{{/if}}
{{/if}}

## ATTRIBUTE DERIVATION

Using your thorough analysis above, derive the following structured attributes.

### Portrait Mode Attributes (all image modes):

- **age**: Estimated age in years (integer). Use your knowledge of {{inspirationName}} if available.
- **gender**: Apparent gender presentation — Female, Male, or Non-binary.
- **skinTone**: Must be one of the following. Use the FULL scale — do not default to middle values:
  - **Porcelain**: Extremely fair and translucent skin, minimal pigmentation.
  - **Ivory**: Very light skin tone, often with cool or slightly rosy undertones.
  - **Beige**: Standard light complexion, neutral fair tone.
  - **Honey**: A warm, light golden-brown tone, radiant complexion.
  - **Sienna**: Medium, rich brown with golden and reddish undertones.
  - **Caramel**: A smooth, deep medium-brown with strong golden or yellow undertones.
  - **Tawny**: A deep, rich golden-brown hue, darker than caramel.
  - **Ebony**: The deepest brown tone, nearing black, with rich undertones.
- **eyeColor**: Must be one of: Brown, Dark Brown, Light Brown, Hazel, Green, Blue, Gray, Amber, Black. Calibration examples: Dark Brown (e.g., Priyanka Chopra), Hazel (e.g., Halle Berry), Blue (e.g., Chris Hemsworth), Gray (e.g., Ian Somerhalder).
- **hairColor**: Must be one of: Black, Dark Brown, Brown, Light Brown, Auburn, Red, Strawberry Blonde, Blonde, Fake Blonde, Platinum Blonde, Gray, White, Salt and Pepper.
  - Use **Fake Blonde** when the person's hair is clearly dyed blonde — look for telltale signs like dark roots, eyebrows that are significantly darker than the hair, an unnaturally uniform blonde tone, or your knowledge that this person is a known brunette/redhead who dyes their hair blonde.
  - Calibration examples: Black (e.g., Lucy Liu), Brown (e.g., Gal Gadot), Platinum Blonde (e.g., Gwen Stefani), Fake Blonde (e.g., Madonna, Kim Kardashian during dyed-blonde eras), Salt and Pepper (e.g., George Clooney).
- **faceDescription**: A detailed paragraph (3-6 sentences) describing the face and hair comprehensively, suitable for recreating the subject with a text-to-image model. Weave together your observations from all facial analysis items (structure, eyes, brows, nose, mouth, hair, skin) into a cohesive, vivid description. Include hair length, texture, and styling details in this paragraph. Do NOT use bullet points — write flowing prose.
- **ethnicities**: Ethnic background(s) as a JSON object (record) mapping L1 groups to arrays of L2 sub-ethnicities. Your knowledge of {{inspirationName}} is particularly valuable here, as ethnicity is often not directly derivable from appearance alone.
  - **IMPORTANT**: Output actual ethnicities, NOT nationalities. Keys MUST be from the official L1 list below. Values MUST be from the corresponding L2 list.
  - For people of mixed heritage, include multiple top-level keys in the record.
  - **L1 groups (use as keys):** Caucasian, East Asian, Southeast Asian, South Asian, Central & North Asian, Middle Eastern, North African, Sub-Saharan African, African-American, Afro-Caribbean (Non-Hispanic), Afro-Latino, Hispanic / Latino, Native American / Indigenous Peoples, Polynesian / Pacific Islander.
  - **L2 sub-ethnicities (use as values):** Use ONLY the official sub-ethnicities for each L1 group. Examples:
    - \`{ "Caucasian": ["Anglo-Saxon (English)"] }\`
    - \`{ "East Asian": ["Yamato / Japanese"] }\`
    - \`{ "African-American": [] }\` (no L2 sub-groups)
    - \`{ "Caucasian": ["Celtic (Irish, Scottish, Welsh, Breton)"], "Sub-Saharan African": ["West African"] }\` (mixed heritage)

{{#if (ne imageMode "portrait")}}
### Half-Body Attributes (half-body and full-body modes):

- **shape**: Must be one of the following. Use the FULL scale. Calibration examples: Slender (e.g., Keira Knightley), Lean (e.g., Michael B. Jordan), Plump (e.g., Queen Latifah), Massive (e.g., Hafþór Júlíus Björnsson).
  - **Skeletal**: Extremely thin to the point of being gaunt.
  - **Thin**: Very slender with little body fat or muscle mass.
  - **Slender**: Gracefully thin; lithe and slim.
  - **Lean**: Low body fat, often with visible but not bulky muscle.
  - **Average**: A balanced build, neither excessively thin nor overweight.
  - **Plump**: Softly rounded; carrying noticeable extra weight.
  - **Overweight**: Clearly carrying substantial excess mass.
  - **Massive**: Extremely large and heavy-set, in height, width, or both.
- **athleticism**: Must be one of the following. Use the FULL scale. Calibration examples: Fit (e.g., Ryan Reynolds), Athletic (e.g., Gal Gadot), Elite (e.g., Serena Williams, Cristiano Ronaldo, Simone Biles).
  - **Atrophied**: Muscles have wasted away due to disuse or illness.
  - **Sedentary**: Inactive; showing no signs of regular physical activity.
  - **Sluggish**: Slow-moving and lacking energy.
  - **Average**: A normal level of fitness; not notably athletic or unathletic.
  - **Fit**: Healthy and in good physical shape.
  - **Toned**: Visible muscle definition without being bulky.
  - **Athletic**: Visibly strong and capable; built for performance and activity.
  - **Elite**: The peak of physical conditioning for a specific sport or activity.
- **chest**: Must be one of the following. Use the FULL scale. Calibration examples: Very Narrow (e.g., Timothée Chalamet), Average (e.g., Leonardo DiCaprio), Very Wide (e.g., Dwayne Johnson).
  - **Very Narrow**: Notably small chest circumference; flat or minimal upper torso width.
  - **Narrow**: Below average chest width; slim upper torso.
  - **Average**: Typical chest proportions for body type.
  - **Wide**: Above average chest width; broad or full upper torso.
  - **Very Wide**: Notably large chest circumference; very broad or full upper torso.
- **bodyDescription**: A detailed paragraph (3-6 sentences) describing the body comprehensively, suitable for recreating the subject with a text-to-image model. Weave together your observations from all body analysis items (build, proportions, neck, shoulders, arms, posture, torso) into a cohesive, vivid description. Do NOT use bullet points — write flowing prose.

{{#if (eq imageMode "full-body")}}
### Full-Body Attributes (full-body mode only):

- **waist**: Must be one of the following. Use the FULL scale. Calibration examples: Very Narrow (e.g., Zendaya), Average (e.g., Jennifer Lawrence), Wide/Very Wide (e.g., Melissa McCarthy in fuller-build eras).
  - **Very Narrow**: Exceptionally small waist; highly defined midsection.
  - **Narrow**: Below average waist circumference; trim midsection.
  - **Average**: Typical waist proportions for body type.
  - **Wide**: Above average waist circumference; thicker midsection.
  - **Very Wide**: Notably large waist; broad midsection.
- **hips**: Must be one of the following. Use the FULL scale. Calibration examples: Very Narrow (e.g., Keira Knightley), Average (e.g., Emma Stone), Wide/Very Wide (e.g., Beyoncé, Sofia Vergara).
  - **Very Narrow**: Notably small hip circumference; very slim lower torso.
  - **Narrow**: Below average hip width; slim lower torso.
  - **Average**: Typical hip proportions for body type.
  - **Wide**: Above average hip width; full lower torso.
  - **Very Wide**: Notably large hip circumference; very full lower torso.
- **height**: Estimated height in centimeters (integer). Your knowledge of {{inspirationName}} is particularly valuable here. Use contextual clues from the image and known facts.
- **bodyArchetype**: Must be one of: Petite Slight, Petite Athletic, Petite Muscular, Short Stocky, Short Heavyset, Compact Lean, Compact Athletic, Compact Muscular, Compact Broad/Stocky, Average Slight, Average Lean, Average Proportional, Average Athletic, Average Muscular, Average Broad, Average Stocky, Average Heavyset, Curvy / Full, Tall Lean, Tall Athletic, Tall Muscular, Tall Broad, Tall Heavyset, Towering Slender, Towering Athletic, Towering Massive. Use your knowledge of {{inspirationName}} to inform this.
{{/if}}
{{/if}}

## GUIDELINES

- Use the FULL range of every scale. Real people vary enormously — do not cluster around "Average" unless it genuinely applies.
- For enum fields, always use EXACTLY one of the provided options.
- For description paragraphs, write rich, detailed prose (3-6 sentences) that a text-to-image model could use to faithfully reproduce the subject's appearance.
- Combine visual observation with your knowledge of {{inspirationName}}. If you have no knowledge of this person, rely on visual observation only and state what you can see.
- If a specific attribute is genuinely ambiguous or not clearly visible, omit it rather than guess.

## OUTPUT

Return a JSON object:
\`\`\`json
{
  "isValid": true,
  "attributes": {
    // Only include attributes appropriate for the image mode
  }
}
\`\`\`
`;
